import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarClock, Loader2, Pencil, Play, Plus, Settings, Trash2, Webhook } from "lucide-react";
import { describeCron } from "@prodgy/schedule";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Badge } from "@/shared/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Button } from "@/shared/components/ui/button";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { DataPagination } from "@/shared/components/DataPagination";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { usePagination } from "@/shared/hooks/usePagination";
import { useToast } from "@/shared/hooks/use-toast";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { workflowSettingsPath } from "@/features/Agents/routes";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { getLocalizedText } from "@/shared/utils/i18nHelper";

import { JobsService } from "../services/jobsService";
import type { Job, JobWebhookHandoff } from "../types/job";
import { EMPTY_JOBS_FILTER, filterJobs, type JobsFilterState } from "../utils/filter";
import { JobFormDialog } from "./JobFormDialog";
import { JobTestDialog } from "./JobTestDialog";
import { useJobRunsEvents } from "../hooks/useJobRunsEvents";
import { JobRunStatusIcon } from "./JobRunStatusIcon";
import { JobRunsTab } from "./JobRunsTab";
import { JobsToolbar } from "./JobsToolbar";
import { WebhookHandoffDialog } from "./WebhookHandoffDialog";

/**
 * A tela Jobs (AB#86605, AC13): o que roda sozinho, seguindo uma Skill, em lista.
 *
 * Todo mundo com acesso ao workspace vê; criar, editar, testar e publicar são
 * de admin — a api decide, e a tela só não oferece o que seria recusado. Os
 * detalhes (versões, execuções, a aba Workflow) abrem nas configurações do
 * workflow, que é o que o Job é por baixo.
 */
export function JobsPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  // A aba fica no endereço: dá para mandar o link do histórico para alguém.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "runs" ? "runs" : "jobs";
  const queryClient = useQueryClient();
  const { productId, isResolving } = useViewScope();
  const { isAdmin } = useIsAdmin();
  const locale = i18n.language.split("-")[0];

  const [formOpen, setFormOpen] = useState(false);
  // A skill de onde o job nasce, quando veio do card dela (task 86874).
  const [seedSkill, setSeedSkill] = useState<string | null>(null);

  // Vindo do card de uma skill (`/jobs?new=1&skill=<nome>`): abre o formulário
  // de novo job com a proposta e tira os parâmetros do endereço, para recarregar
  // a página não abrir o formulário de novo.
  useEffect(() => {
    const skill = searchParams.get("skill");
    if (searchParams.get("new") !== "1" || !skill || !isAdmin) return;
    setEditing(null);
    setSeedSkill(skill);
    setFormOpen(true);
    const rest = new URLSearchParams(searchParams);
    rest.delete("new");
    rest.delete("skill");
    setSearchParams(rest, { replace: true });
  }, [searchParams, isAdmin, setSearchParams]);
  const [editing, setEditing] = useState<Job | null>(null);
  const [testing, setTesting] = useState<Job | null>(null);
  const [publishing, setPublishing] = useState<Job | null>(null);
  // O job sendo publicado agora: segura o diálogo aberto e gira o botão da linha.
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<JobWebhookHandoff | null>(null);
  const [deleting, setDeleting] = useState<Job | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [filter, setFilter] = useState<JobsFilterState>(EMPTY_JOBS_FILTER);
  // Paginação na tela, como o padrão das listas: os jobs de um workspace vêm
  // inteiros, e o filtro corre sobre todos antes de paginar.
  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<Job>({
    initialPageSize: 10,
  });
  useEffect(() => setCurrentPage(1), [filter, setCurrentPage]);

  const jobsQuery = useQuery({
    queryKey: ["jobs", productId],
    queryFn: () => JobsService.list(productId!),
    enabled: Boolean(productId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["jobs", productId] });

  // O status das execuções chega pelo SSE (task 86870): a lista do histórico, a
  // execução aberta no painel e a "última execução" de cada job se atualizam na
  // hora. Sem conexão, as consultas periódicas seguem como reserva.
  const { connected: runsRealtime } = useJobRunsEvents(productId, (change) => {
    void queryClient.invalidateQueries({ queryKey: ["job-runs", productId] });
    void queryClient.invalidateQueries({ queryKey: ["job-run", productId, change.execution_id] });
    void queryClient.invalidateQueries({ queryKey: ["jobs", productId] });
  });

  // O diálogo só fecha depois que a lista já mostra o job publicado: fechar
  // antes deixava a linha em Draft por alguns segundos, sem sinal de que algo
  // estava acontecendo.
  const handlePublish = async () => {
    if (!publishing || !productId) return;
    const job = publishing;
    setPublishingId(job.id);
    try {
      await JobsService.publish(productId, job.id);
      await refresh();
      toast({ title: t("jobs.publish.success") });
      setTesting(null);
      setPublishing(null);
    } catch (error) {
      toast({ title: resolveApiErrorMessage(error, t), variant: "destructive" });
    } finally {
      setPublishingId(null);
    }
  };

  // Como na publicação, o diálogo só fecha quando a lista já não mostra o job.
  const handleDelete = async () => {
    if (!deleting || !productId) return;
    const job = deleting;
    setDeletingBusy(true);
    try {
      await JobsService.remove(productId, job.id);
      await refresh();
      void queryClient.invalidateQueries({ queryKey: ["job-runs", productId] });
      toast({ title: t("jobs.delete.success", { name: job.name }) });
      setDeleting(null);
    } catch (error) {
      toast({ title: resolveApiErrorMessage(error, t), variant: "destructive" });
    } finally {
      setDeletingBusy(false);
    }
  };

  if (isResolving) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!productId) {
    return <p className="p-6 text-sm text-muted-foreground">{t("jobs.noWorkspace")}</p>;
  }

  const jobs = jobsQuery.data ?? [];
  const visible = filterJobs(jobs, filter);

  // O título vem do breadcrumb da navbar, como nas outras telas do menu; aqui
  // fica só a ação, na altura dos filtros (`h-9`, sem ceder largura).
  const newJob = isAdmin && (
    <Button
      className="h-9 shrink-0"
      onClick={() => {
        setEditing(null);
        setSeedSkill(null);
        setFormOpen(true);
      }}
      data-tour="new-job-button"
    >
      <Plus className="mr-2 h-4 w-4" /> {t("jobs.newJob")}
    </Button>
  );

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setSearchParams(value === "runs" ? { tab: "runs" } : {}, { replace: true })}
      className="space-y-6"
    >
      <TabsList>
        <TabsTrigger value="jobs">{t("jobs.tabs.jobs")}</TabsTrigger>
        <TabsTrigger value="runs">{t("jobs.tabs.runs")}</TabsTrigger>
      </TabsList>

      <TabsContent value="runs" className="mt-0">
        <JobRunsTab productId={productId} jobs={jobs} realtime={runsRealtime} />
      </TabsContent>

      <TabsContent value="jobs" className="mt-0 space-y-6">
      {jobs.length > 0 && (
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <JobsToolbar value={filter} onChange={setFilter} total={visible.length} />
          </div>
          {newJob}
        </div>
      )}

      {jobsQuery.isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : jobsQuery.isError ? (
        <p className="text-sm text-destructive">{t("jobs.loadFailed")}</p>
      ) : jobs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <CalendarClock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-medium">{t("jobs.empty.title")}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t("jobs.empty.description")}</p>
          {isAdmin && <p className="mt-2 text-sm text-muted-foreground">{t("jobs.empty.adminHint")}</p>}
          {newJob && <div className="mt-4">{newJob}</div>}
        </div>
      ) : visible.length === 0 ? null : (
        <div className="rounded-lg border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[24%]">{t("jobs.columns.name")}</TableHead>
                <TableHead className="w-[30%]">{t("jobs.columns.instruction")}</TableHead>
                <TableHead>{t("jobs.columns.trigger")}</TableHead>
                <TableHead>{t("jobs.columns.status")}</TableHead>
                <TableHead className="whitespace-nowrap">{t("jobs.columns.lastRun")}</TableHead>
                <TableHead className="text-center">{t("jobs.columns.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginateData(visible).map((job) => (
                <TableRow key={job.id} className={job.published ? undefined : "text-muted-foreground"}>
                  <TableCell className="max-w-0">
                    <p className="truncate font-medium text-foreground" title={job.name}>{job.name}</p>
                    {job.description && (
                      <p className="truncate text-xs" title={job.description}>
                        {job.description}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="max-w-0">
                    <p className="line-clamp-2 text-sm" title={job.instruction}>
                      {job.instruction}
                    </p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 text-sm">
                      {job.trigger.type === "webhook" ? (
                        <Webhook className="h-3.5 w-3.5" />
                      ) : (
                        <CalendarClock className="h-3.5 w-3.5" />
                      )}
                      {/* O ícone já diz o tipo: no horário fica só a frase, com o cron no tooltip. */}
                      {job.trigger.type === "schedule" ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted underline-offset-4">
                              {scheduleText(job, locale)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <code className="font-mono text-xs">{job.trigger.cron}</code>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span>{t("agents.trigger.types.webhook")}</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    {job.published ? (
                      <Badge className="bg-green-600 hover:bg-green-600">{t("jobs.status.published")}</Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-dashed text-[11px] uppercase tracking-wide text-muted-foreground"
                      >
                        {t("jobs.status.draft")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {job.last_run?.at ? (
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <JobRunStatusIcon status={job.last_run.status} />
                        {formatDistanceToNow(new Date(job.last_run.at), {
                          addSuffix: true,
                          locale: locale === "pt" ? ptBR : undefined,
                        })}
                      </span>
                    ) : (
                      t("jobs.lastRun.never")
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center gap-1">
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="ghost" title={t("jobs.actions.test")} onClick={() => setTesting(job)}>
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t("jobs.actions.edit")}
                            onClick={() => {
                              setEditing(job);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        title={t("jobs.actions.details")}
                        onClick={() => navigate(workflowSettingsPath(job.id))}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title={t("jobs.actions.delete")}
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleting(job)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DataPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={visible.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}
      </TabsContent>

      <JobFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) {
            setSeedSkill(null);
            // O Testar grava um rascunho; fechar sem publicar não pode escondê-lo da lista.
            void refresh();
          }
        }}
        productId={productId}
        job={editing}
        seedSkill={seedSkill}
        onSaved={({ handoff: next }) => {
          setFormOpen(false);
          setEditing(null);
          setSeedSkill(null);
          if (next) setHandoff(next);
          void refresh();
        }}
      />

      <JobTestDialog
        job={testing}
        productId={productId}
        canPublish={isAdmin}
        onClose={() => {
          setTesting(null);
          void refresh();
        }}
        onPublish={(job) => setPublishing(job)}
      />

      <AlertDialog
        open={Boolean(publishing)}
        onOpenChange={(open) => !open && !publishingId && setPublishing(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.publish.confirmTitle", { name: publishing?.name ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("jobs.publish.confirmDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(publishingId)}>{t("jobs.publish.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(publishingId)}
              onClick={(event) => {
                // Sem isto o Radix fecha o diálogo no clique, antes da publicação terminar.
                event.preventDefault();
                void handlePublish();
              }}
            >
              {publishingId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("jobs.publish.publishing")}
                </>
              ) : (
                t("jobs.publish.confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && !deletingBusy && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.delete.confirmTitle", { name: deleting?.name ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                deleting?.trigger.type === "webhook"
                  ? "jobs.delete.confirmDescriptionWebhook"
                  : "jobs.delete.confirmDescription",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingBusy}>{t("jobs.delete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              {deletingBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("jobs.delete.deleting")}
                </>
              ) : (
                t("jobs.delete.confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WebhookHandoffDialog handoff={handoff} onClose={() => setHandoff(null)} />
    </Tabs>
  );
}

/**
 * A frase do agendamento no idioma da pessoa (AB#87330). A api a envia como
 * `{ pt, en }`; sem ela (api anterior), a tela descreve com as mesmas regras.
 */
function scheduleText(job: Job, locale: string): string {
  if (job.trigger.type !== "schedule") return "";
  if (job.schedule_description) return getLocalizedText(job.schedule_description, locale);
  return describeCron(job.trigger.cron, {
    locale: locale === "pt" ? "pt" : "en",
    timezone: job.trigger.timezone,
  });
}

export default JobsPage;
